import { makeStyles, Theme, Box, Divider } from '@material-ui/core';
import React from 'react';
import EditPencil from '../../components/EditPencil';
import CardSheet from '../../components/CardSheet';
import { Geography, Industry, MarketModelCriteria } from '../../types/types';
import IndustryBullet from '../../components/IndustryBullet';
import { StyledText } from '../../components/StyledText';
import { getShorthandDisplayedLocation } from '../../lib';
import { tbd, convertDollarsToMillions, formatDollarsAsMillions } from '../../lib/utils';


interface UnpublishedInvestorInvestmentCriteria {
  investmentLow: number | null
  investmentHigh: number | null
  investmentRevenue: number | null
  founderFemale: boolean | null
  founderUnderrepresented: boolean | null
  marketModelCriteria: MarketModelCriteria
  industryList: Industry[] | null
  geographyList: Geography[] | null
}
interface InvestorCriteriaPanelProps {
  criteria: UnpublishedInvestorInvestmentCriteria
  handleEdit?: () => void
}


const useStyles = makeStyles((theme: Theme) => ({
  root: {
    margin: theme.spacing(0),
    position: 'relative'
  },
  investmentCriteria: {
    margin: 0
  }
}));

const InvestorCriteriaPanel: React.FC<InvestorCriteriaPanelProps> = ({
  criteria,
  handleEdit
}) => {
  const styles = useStyles();
  const editable = handleEdit !== undefined;
  const { marketModelCriteria, industryList, geographyList, investmentRevenue, investmentLow, investmentHigh, founderFemale, founderUnderrepresented } = criteria;

  const getGeographyString = () => {
    if (!geographyList || !geographyList.length) {
      return tbd;
    }

    const firstState = geographyList.find((v) => v.state);
    return geographyList.map((v) => {
      if (v === firstState) {
        return 'USA: ' + getShorthandDisplayedLocation(v);
      } else {
        return getShorthandDisplayedLocation(v);
      }
    }).join(', ');
  };

  const geogString = getGeographyString();

  const getInvestmentRangeString = () => {
    if (investmentLow !== null && investmentHigh !== null) {
      return `$${convertDollarsToMillions(investmentLow)}M - $${convertDollarsToMillions(investmentHigh)}M`;
    }
    return tbd;
  };

  const getInvestmentRev = () => {
    if (investmentRevenue === null) {
      return tbd;
    }
    return formatDollarsAsMillions(investmentRevenue);
  };

  const getFounderFemale = () => {
    if (founderFemale === null) {
      return tbd;
    }
    return founderFemale ? 'Yes' : 'No';
  };

  const getUR = () => {
    if (founderUnderrepresented === null) {
      return tbd;
    }
    return founderUnderrepresented ? 'Yes' : 'No';
  };

  const IndustryListElement = () => {
    if (!industryList || !industryList.length) {
      return (<StyledText gutterBottom>{tbd}</StyledText>);
    }
    return (
      <>
        {
          industryList.map((v, k) => (
            <IndustryBullet padding='0' variant='body1' industry={v.industry} key={k} />
          ))
        }
      </>
    );
  };

  const MarketModelItem = () => {
    if (marketModelCriteria) {
      const { B2B, B2C, B2G, SaaS } = marketModelCriteria;
      if (B2B && B2C && B2G && SaaS) {
        return (
          <>
            <IndustryBullet padding='0' variant='body1' industry={`${B2B} invest in B2B`} />
            <IndustryBullet padding='0' variant='body1' industry={`${B2C} invest in B2C`} />
            <IndustryBullet padding='0' variant='body1' industry={`${B2G} invest in B2G`} />
            <IndustryBullet padding='0' variant='body1' industry={`${SaaS} invest in SaaS`} />
          </>
        );
      }
    }
    return (<StyledText gutterBottom>{tbd}</StyledText>);
  };

  return (
    <div className={styles.root}>
      {editable && <EditPencil onClick={handleEdit} style={{ top: 10, right: 10, position: 'absolute' }} />}
      <CardSheet>
        <Box padding='50px 55px' className={styles.investmentCriteria}>
          <StyledText variant='h2'>
            Investment Criteria
          </StyledText>
          <Box display='flex' flexDirection='row' justifyContent='center'>
            <Box flex='1 1 50%' padding='15px 20px 15px 0'>
              <StyledText gutterBottom variant='h5' color='primary'>Investment Amount</StyledText>
              <StyledText gutterBottom>{getInvestmentRangeString()}</StyledText>
              <Divider style={{ marginTop: 10, marginBottom: 10 }} />
              <StyledText gutterBottom variant='h5' color='primary'>Min Current Revenue</StyledText>
              <StyledText gutterBottom>{getInvestmentRev()}</StyledText>
              <Divider style={{ marginTop: 10, marginBottom: 10 }} />
              <StyledText gutterBottom variant='h5' color='primary'>Location</StyledText>
              <StyledText gutterBottom style={{ textTransform: geogString === tbd ? 'none' : 'capitalize' }}>
                {geogString}
              </StyledText>
              <Divider style={{ marginTop: 10, marginBottom: 10 }} />
              <StyledText gutterBottom variant='h5' color='primary'>Industries</StyledText>
              <IndustryListElement />
            </Box>
            <Box flex='1 1 50%' padding='15px 0 15px 20px'>
              <StyledText gutterBottom variant='h5' color='primary'>Market Model</StyledText>
              <MarketModelItem />
              <Divider style={{ marginTop: 10, marginBottom: 10 }} />
              <StyledText gutterBottom variant='h5' color='primary'>Requires Female Founder</StyledText>
              <StyledText gutterBottom>{getFounderFemale()}</StyledText>
              <Divider style={{ marginTop: 10, marginBottom: 10 }} />
              <StyledText variant='h5' color='primary'>Requires Underrepresented Management Team</StyledText>
              <StyledText gutterBottom>{getUR()}</StyledText>
            </Box>
          </Box>
        </Box>
      </CardSheet>
    </div>
  );
};


export default InvestorCriteriaPanel;
